import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountTree as LayoutIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ImageSearch as ImportImageIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// ── Try to import @xyflow/react (graceful degradation if not installed) ────────

let ReactFlowLib = null;
try {
  ReactFlowLib = require('@xyflow/react');
  require('@xyflow/react/dist/style.css');
} catch {
  // package not yet installed
}

// ── Node type config ──────────────────────────────────────────────────────────

const NODE_TYPE_CONFIG = {
  application: { label: 'Application', shape: 'diamond' },
  outcome:     { label: 'Outcome',     shape: 'circle'  },
  database:    { label: 'Database',    shape: 'rect'    },
  api:         { label: 'API',         shape: 'pill'    },
  file:        { label: 'File',        shape: 'rect'    },
};

const NODE_TYPES_LIST = Object.keys(NODE_TYPE_CONFIG);

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch { return iso; }
}

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ── Force-directed auto-layout ────────────────────────────────────────────────

function runForceLayout(nodes, edges, iterations = 60) {
  const REPULSION = 9000;
  const SPRING_LEN = 220;
  const SPRING_K = 0.04;
  const DAMPING = 0.82;

  const pos = {};
  const vel = {};
  nodes.forEach((n) => {
    pos[n.id] = { x: n.position.x, y: n.position.y };
    vel[n.id] = { x: 0, y: 0 };
  });

  for (let iter = 0; iter < iterations; iter++) {
    const force = {};
    nodes.forEach((n) => { force[n.id] = { x: 0, y: 0 }; });

    // Repulsion between all node pairs
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i], b = nodes[j];
        const dx = pos[b.id].x - pos[a.id].x;
        const dy = pos[b.id].y - pos[a.id].y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const f = REPULSION / (dist * dist);
        const fx = (dx / dist) * f;
        const fy = (dy / dist) * f;
        force[a.id].x -= fx;
        force[a.id].y -= fy;
        force[b.id].x += fx;
        force[b.id].y += fy;
      }
    }

    // Spring attraction along edges
    edges.forEach((e) => {
      const a = pos[e.source], b = pos[e.target];
      if (!a || !b) return;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const f = SPRING_K * (dist - SPRING_LEN);
      const fx = (dx / dist) * f;
      const fy = (dy / dist) * f;
      force[e.source].x += fx;
      force[e.source].y += fy;
      force[e.target].x -= fx;
      force[e.target].y -= fy;
    });

    // Update velocities and positions
    nodes.forEach((n) => {
      vel[n.id].x = (vel[n.id].x + force[n.id].x) * DAMPING;
      vel[n.id].y = (vel[n.id].y + force[n.id].y) * DAMPING;
      pos[n.id].x += vel[n.id].x;
      pos[n.id].y += vel[n.id].y;
    });
  }

  return pos;
}

// ── Create map dialog ─────────────────────────────────────────────────────────

function CreateMapDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setName(''); setDescription(''); setError(''); };
  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Please enter a name.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/data-maps/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim() }),
      });
      if (!res.ok) throw new Error(`Failed to create map (${res.status})`);
      onCreate(await res.json());
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Data Map</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
        />
        <TextField
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Import from image dialog ──────────────────────────────────────────────────

function ImportImageDialog({ open, onClose, onImport, showNameField = true }) {
  const [file, setFile] = useState(null);
  const [mapName, setMapName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isVisio = file && file.name.toLowerCase().endsWith('.vsdx');

  const reset = () => { setFile(null); setMapName(''); setError(''); };
  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      if (!mapName) setMapName(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!file) { setError('Please select a file.'); return; }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = isVisio ? 'extract-visio' : 'extract-image';
      const res = await fetch(`${API_BASE}/api/data-maps/${endpoint}`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Extraction failed');
      onImport({ name: mapName.trim() || 'Imported Map', nodes: data.nodes, edges: data.edges });
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadingText = isVisio ? 'Parsing Visio file…' : 'Analysing image…';
  const chooseLabel = file ? file.name : 'Choose Image or Visio File';

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Import from Image or Visio</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">{loadingText}</Typography>
          </Box>
        ) : (
          <>
            <Button variant="outlined" component="label" startIcon={<ImportImageIcon />}>
              {chooseLabel}
              <input type="file" hidden accept="image/*,.vsdx" onChange={handleFileChange} />
            </Button>
            {showNameField && (
              <TextField
                label="Map Name"
                value={mapName}
                onChange={(e) => setMapName(e.target.value)}
                fullWidth
                size="small"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {isVisio
                ? 'Visio (.vsdx) file detected — shapes and connectors will be extracted automatically.'
                : 'Upload a screenshot or photo of any data flow or architecture diagram. Claude AI will extract the nodes and connections automatically. Visio .vsdx files are also supported.'}
            </Typography>
          </>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!file}>
            Import
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

// ── Inner graph (only rendered when @xyflow/react is available) ────────────────

const NODE_INDICATOR = ({ data }) => {
  const total = (data.docLinks?.length || 0) + (data.appLinks?.length || 0) + (data.outcomeLinks?.length || 0);
  return (data.notes || total > 0) ? (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, alignItems: 'center' }}>
      {data.notes && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />}
      {total > 0 && (
        <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', lineHeight: 1 }}>
          {total}d
        </Typography>
      )}
    </Box>
  ) : null;
};

function makeDataMapInner(rf) {
  const {
    ReactFlow, Background, Controls, MiniMap, Handle, Position,
    Panel, addEdge, useNodesState, useEdgesState,
  } = rf;

  // ── Node renderers ────────────────────────────────────────────────────────

  function ApplicationNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box sx={{ width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'default' }}>
        <Handle type="source" position={Position.Right}  style={{ background: '#ff9800', zIndex: 10 }} />
        <Handle type="target" position={Position.Left}   style={{ background: '#ff9800', zIndex: 10 }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#ff9800', zIndex: 10 }} />
        <Handle type="target" position={Position.Top}    style={{ background: '#ff9800', zIndex: 10 }} />
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: dark ? '#2b1a00' : '#fff3e0', border: `2px solid ${data.selected ? (dark ? '#ffb74d' : '#e65100') : '#ffa726'}`, transform: 'rotate(45deg)', borderRadius: 1 }} />
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center', px: 1, lineHeight: 1.3 }}>{data.label}</Typography>
          <NODE_INDICATOR data={data} />
        </Box>
      </Box>
    );
  }

  function OutcomeNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box sx={{ width: 110, height: 110, bgcolor: dark ? '#0d1f3c' : '#e3f2fd', border: `2px solid ${data.selected ? (dark ? '#90caf9' : '#0d47a1') : '#42a5f5'}`, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', cursor: 'default' }}>
        <Handle type="source" position={Position.Right}  style={{ background: '#42a5f5' }} />
        <Handle type="target" position={Position.Left}   style={{ background: '#42a5f5' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#42a5f5' }} />
        <Handle type="target" position={Position.Top}    style={{ background: '#42a5f5' }} />
        <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3, px: 1 }}>{data.label}</Typography>
        <NODE_INDICATOR data={data} />
      </Box>
    );
  }

  function DatabaseNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box sx={{ px: 1.5, py: 1, minWidth: 110, maxWidth: 150, bgcolor: dark ? '#0d2926' : '#e0f2f1', border: `2px solid ${data.selected ? (dark ? '#80cbc4' : '#00695c') : '#4db6ac'}`, borderRadius: 1.5, cursor: 'default' }}>
        <Handle type="source" position={Position.Right}  style={{ background: '#4db6ac' }} />
        <Handle type="target" position={Position.Left}   style={{ background: '#4db6ac' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#4db6ac' }} />
        <Handle type="target" position={Position.Top}    style={{ background: '#4db6ac' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>{data.label}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>Database</Typography>
        <NODE_INDICATOR data={data} />
      </Box>
    );
  }

  function ApiNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box sx={{ px: 2, py: 0.75, minWidth: 90, bgcolor: dark ? '#1e0d2e' : '#f3e5f5', border: `2px solid ${data.selected ? (dark ? '#ce93d8' : '#4a148c') : '#ab47bc'}`, borderRadius: 3, cursor: 'default' }}>
        <Handle type="source" position={Position.Right}  style={{ background: '#ab47bc' }} />
        <Handle type="target" position={Position.Left}   style={{ background: '#ab47bc' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#ab47bc' }} />
        <Handle type="target" position={Position.Top}    style={{ background: '#ab47bc' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>{data.label}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>API</Typography>
        <NODE_INDICATOR data={data} />
      </Box>
    );
  }

  function FileNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box sx={{ px: 1.5, py: 1, minWidth: 100, maxWidth: 150, bgcolor: dark ? '#1a1a1a' : '#f5f5f5', border: `2px solid ${data.selected ? (dark ? '#bdbdbd' : '#616161') : '#90a4ae'}`, borderRadius: 1, cursor: 'default' }}>
        <Handle type="source" position={Position.Right}  style={{ background: '#90a4ae' }} />
        <Handle type="target" position={Position.Left}   style={{ background: '#90a4ae' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#90a4ae' }} />
        <Handle type="target" position={Position.Top}    style={{ background: '#90a4ae' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>{data.label}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>File</Typography>
        <NODE_INDICATOR data={data} />
      </Box>
    );
  }

  const NODE_TYPES = {
    applicationNode: ApplicationNode,
    outcomeNode:     OutcomeNode,
    databaseNode:    DatabaseNode,
    apiNode:         ApiNode,
    fileNode:        FileNode,
  };

  // ── Add Node panel ────────────────────────────────────────────────────────

  function AddNodePanel({ context, onAdd, onClose }) {
    const [nodeType, setNodeType] = useState('database');
    const [label, setLabel] = useState('');

    const contextOptions = useMemo(() => {
      if (nodeType === 'application') return (context.applications || []).map(a => a.name);
      if (nodeType === 'outcome') return (context.outcomes || []).map(o => o.name);
      return [];
    }, [nodeType, context]);

    const handleAdd = () => {
      if (!label.trim()) return;
      onAdd(nodeType, label.trim());
      setLabel('');
    };

    const TYPE_COLORS = { application: '#ffa726', outcome: '#42a5f5', database: '#4db6ac', api: '#ab47bc', file: '#90a4ae' };

    return (
      <Paper elevation={4} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10, p: 1.5, width: 240, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" fontWeight={700}>Add Node</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {NODE_TYPES_LIST.map((t) => (
            <Chip key={t} label={NODE_TYPE_CONFIG[t].label} size="small"
              onClick={() => { setNodeType(t); setLabel(''); }}
              sx={{ bgcolor: nodeType === t ? TYPE_COLORS[t] : undefined, color: nodeType === t ? '#fff' : undefined, fontWeight: nodeType === t ? 700 : 400, fontSize: '0.65rem' }}
            />
          ))}
        </Box>
        {contextOptions.length > 0 ? (
          <Autocomplete size="small" freeSolo options={contextOptions} value={label}
            onInputChange={(_, v) => setLabel(v)} onChange={(_, v) => setLabel(v || '')}
            renderInput={(params) => <TextField {...params} label="Name" autoFocus />}
          />
        ) : (
          <TextField size="small" label="Name" value={label} onChange={(e) => setLabel(e.target.value)}
            autoFocus onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
        )}
        <Button variant="contained" size="small" onClick={handleAdd} disabled={!label.trim()}>Add</Button>
      </Paper>
    );
  }

  // ── Legend ────────────────────────────────────────────────────────────────

  function Legend() {
    const entries = [
      { color: '#ffa726', label: 'Application' },
      { color: '#42a5f5', label: 'Outcome' },
      { color: '#4db6ac', label: 'Database' },
      { color: '#ab47bc', label: 'API' },
      { color: '#90a4ae', label: 'File' },
    ];
    return (
      <Paper elevation={2} sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="caption" fontWeight={700}>Legend</Typography>
        {entries.map(({ color, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5 }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
      </Paper>
    );
  }

  // ── Node detail panel ─────────────────────────────────────────────────────

  function NodeDetailPanel({ node, nodes, edges, documents, context, isReadOnly, onUpdate, onClose, onDelete }) {
    const [labelInput, setLabelInput] = useState(node.data.label);

    useEffect(() => { setLabelInput(node.data.label); }, [node.id, node.data.label]);

    const connectedNodes = useMemo(() => {
      const out = edges.filter(e => e.source === node.id)
        .map(e => ({ n: nodes.find(n => n.id === e.target), dir: 'out' }));
      const inc = edges.filter(e => e.target === node.id)
        .map(e => ({ n: nodes.find(n => n.id === e.source), dir: 'in' }));
      return [...out, ...inc].filter(c => c.n);
    }, [node.id, nodes, edges]);

    const nodeType = node.type?.replace('Node', '') || 'node';
    const TYPE_COLORS = { application: '#ffa726', outcome: '#42a5f5', database: '#4db6ac', api: '#ab47bc', file: '#90a4ae' };
    const typeColor = TYPE_COLORS[nodeType] || '#90a4ae';

    return (
      <Paper
        elevation={4}
        sx={{
          position: 'absolute', right: 0, top: 0, height: '100%', width: 280,
          zIndex: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          borderLeft: '1px solid', borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Chip label={NODE_TYPE_CONFIG[nodeType]?.label || nodeType} size="small" sx={{ bgcolor: typeColor, color: '#fff', fontSize: '0.6rem', height: 18 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onBlur={() => { if (labelInput.trim() && labelInput !== node.data.label) onUpdate(node.id, { label: labelInput.trim() }); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              disabled={isReadOnly}
              variant="standard"
              fullWidth
              inputProps={{ style: { fontWeight: 600, fontSize: '0.8rem' } }}
            />
          </Box>
          {!isReadOnly && onDelete && (
            <Tooltip title="Delete node">
              <IconButton size="small" color="error" onClick={() => onDelete(node.id)}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Notes */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Notes</Typography>
            <TextField
              size="small"
              multiline
              rows={4}
              fullWidth
              placeholder={isReadOnly ? '—' : 'Add notes about this node…'}
              value={node.data.notes || ''}
              onChange={(e) => onUpdate(node.id, { notes: e.target.value })}
              disabled={isReadOnly}
            />
          </Box>

          <Divider />

          {/* Linked documents */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Linked Documents</Typography>
            {documents.length === 0 ? (
              <Typography variant="caption" color="text.disabled">No documents in Doc Hub yet.</Typography>
            ) : (
              <Autocomplete
                multiple
                size="small"
                options={documents.map(d => d.id)}
                getOptionLabel={(id) => documents.find(d => d.id === id)?.name || id}
                value={node.data.docLinks || []}
                onChange={(_, newVal) => onUpdate(node.id, { docLinks: newVal })}
                disabled={isReadOnly}
                renderInput={(params) => <TextField {...params} placeholder="Search documents…" />}
                renderTags={(value, getTagProps) =>
                  value.map((id, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={id}
                      label={documents.find(d => d.id === id)?.name || id}
                      size="small"
                    />
                  ))
                }
              />
            )}
          </Box>

          {/* Linked applications */}
          {(context.applications?.length > 0) && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Linked Applications</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={(context.applications || []).map(a => a.id)}
                  getOptionLabel={(id) => (context.applications || []).find(a => a.id === id)?.name || id}
                  value={node.data.appLinks || []}
                  onChange={(_, newVal) => onUpdate(node.id, { appLinks: newVal })}
                  disabled={isReadOnly}
                  renderInput={(params) => <TextField {...params} placeholder="Search applications…" />}
                  renderTags={(value, getTagProps) =>
                    value.map((id, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={id}
                        label={(context.applications || []).find(a => a.id === id)?.name || id}
                        size="small"
                        sx={{ bgcolor: '#ffa72622' }}
                      />
                    ))
                  }
                />
              </Box>
            </>
          )}

          {/* Linked outcomes */}
          {(context.outcomes?.length > 0) && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Linked Outcomes</Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={(context.outcomes || []).map(o => o.id)}
                  getOptionLabel={(id) => (context.outcomes || []).find(o => o.id === id)?.name || id}
                  value={node.data.outcomeLinks || []}
                  onChange={(_, newVal) => onUpdate(node.id, { outcomeLinks: newVal })}
                  disabled={isReadOnly}
                  renderInput={(params) => <TextField {...params} placeholder="Search outcomes…" />}
                  renderTags={(value, getTagProps) =>
                    value.map((id, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={id}
                        label={(context.outcomes || []).find(o => o.id === id)?.name || id}
                        size="small"
                        sx={{ bgcolor: '#42a5f522' }}
                      />
                    ))
                  }
                />
              </Box>
            </>
          )}

          {/* Connected nodes */}
          {connectedNodes.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Connected Nodes</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {connectedNodes.map(({ n, dir }) => {
                    const t = n.type?.replace('Node', '') || 'node';
                    return (
                      <Box key={n.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>
                          {dir === 'out' ? '→' : '←'}
                        </Typography>
                        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: TYPE_COLORS[t] || '#90a4ae', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{n.data.label}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', ml: 'auto' }}>
                          {NODE_TYPE_CONFIG[t]?.label || t}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    );
  }

  // ── Main editor component ─────────────────────────────────────────────────

  return function DataMapEditor({ map, context, documents, isReadOnly, onSave, onBack }) {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [addPanelOpen, setAddPanelOpen] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [editorImportOpen, setEditorImportOpen] = useState(false);

    // Populate from loaded map
    useEffect(() => {
      setNodes(
        (map.nodes || []).map((n) => ({
          id: n.id,
          type: `${n.type}Node`,
          position: { x: n.x, y: n.y },
          data: { label: n.label, contextId: n.contextId || null, notes: n.notes || '', docLinks: n.doc_links || [], appLinks: n.app_links || [], outcomeLinks: n.outcome_links || [] },
        }))
      );
      setEdges(
        (map.edges || []).map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          label: e.label || '',
        }))
      );
      setIsDirty(false);
      setSelectedNodeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map.id]);

    // Clear selection if selected node is deleted
    useEffect(() => {
      if (selectedNodeId && !nodes.find(n => n.id === selectedNodeId)) {
        setSelectedNodeId(null);
      }
    }, [nodes, selectedNodeId]);

    const handleNodesChange = useCallback((changes) => {
      onNodesChange(changes);
      if (changes.some((c) => c.type !== 'select')) setIsDirty(true);
    }, [onNodesChange]);

    const handleEdgesChange = useCallback((changes) => {
      onEdgesChange(changes);
      if (changes.some((c) => c.type !== 'select')) setIsDirty(true);
    }, [onEdgesChange]);

    const handleConnect = useCallback((params) => {
      setEdges((eds) => addEdge({ ...params, id: genId() }, eds));
      setIsDirty(true);
    }, [setEdges]);

    const handleNodeClick = useCallback((_, node) => {
      setAddPanelOpen(false);
      setSelectedNodeId(node.id);
    }, []);

    const handleNodeUpdate = useCallback((nodeId, patch) => {
      setNodes((nds) => nds.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n));
      setIsDirty(true);
    }, [setNodes]);

    const handleAddNode = useCallback((type, label) => {
      const newNode = {
        id: genId(),
        type: `${type}Node`,
        position: { x: 200 + Math.random() * 200, y: 200 + Math.random() * 200 },
        data: { label, contextId: null, notes: '', docLinks: [], appLinks: [], outcomeLinks: [] },
      };
      setNodes((nds) => [...nds, newNode]);
      setIsDirty(true);
    }, [setNodes]);

    const handleLayout = useCallback(() => {
      if (nodes.length < 2) return;
      const pos = runForceLayout(nodes, edges);
      setNodes((nds) => nds.map((n) => ({ ...n, position: pos[n.id] || n.position })));
      setIsDirty(true);
    }, [nodes, edges, setNodes]);

    const handleDeleteNode = useCallback((nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId(null);
      setIsDirty(true);
    }, [setNodes, setEdges]);

    const handleEdgeClick = useCallback((event, edge) => {
      if (isReadOnly) return;
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      setIsDirty(true);
    }, [isReadOnly, setEdges]);

    const reactFlowRef = useRef(null);

    const handleExportImage = async () => {
      const wrapper = reactFlowRef.current;
      if (!wrapper) return;
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(wrapper, {
          backgroundColor: null,
          scale: 2,
          useCORS: true,
          ignoreElements: (el) => el.classList?.contains('react-flow__minimap'),
        });
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${(map.name || 'datamap').replace(/[^a-z0-9_-]/gi, '_')}.png`;
        a.click();
      } catch {
        setSaveError('Image export failed.');
      }
    };

    const handleEditorImport = useCallback(({ nodes: newNodes, edges: newEdges }) => {
      const maxX = nodes.reduce((m, n) => Math.max(m, n.position.x), 0);
      const xOffset = maxX > 0 ? maxX + 200 : 0;
      const rfNodes = newNodes.map(n => ({
        id: n.id,
        type: `${n.type}Node`,
        position: { x: n.x + xOffset, y: n.y },
        data: { label: n.label, contextId: null, notes: '', docLinks: [], appLinks: [], outcomeLinks: [] },
      }));
      const rfEdges = newEdges.map(e => ({
        id: e.id, source: e.source, target: e.target, label: e.label || '',
      }));
      setNodes(nds => [...nds, ...rfNodes]);
      setEdges(eds => [...eds, ...rfEdges]);
      setIsDirty(true);
    }, [nodes, setNodes, setEdges]);

    const handleSave = async () => {
      setSaving(true);
      setSaveError('');
      try {
        await onSave({
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type.replace('Node', ''),
            label: n.data.label,
            x: n.position.x,
            y: n.position.y,
            contextId: n.data.contextId || null,
            notes: n.data.notes || '',
            doc_links: n.data.docLinks || [],
            app_links: n.data.appLinks || [],
            outcome_links: n.data.outcomeLinks || [],
          })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            label: e.label || '',
          })),
        });
        setIsDirty(false);
      } catch (err) {
        setSaveError(err.message);
      } finally {
        setSaving(false);
      }
    };

    const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;

    return (
      <Box sx={{ height: 'calc(100vh - 200px)', minHeight: 500, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, px: 0, flexShrink: 0 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small" variant="text">Back</Button>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>{map.name}</Typography>
          {saveError && <Typography variant="caption" color="error">{saveError}</Typography>}
          {!isReadOnly && (
            <Button
              variant={isDirty ? 'contained' : 'outlined'}
              size="small"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving…' : isDirty ? '● Save' : 'Saved'}
            </Button>
          )}
          {!isReadOnly && nodes.length >= 2 && (
            <Tooltip title="Auto-arrange nodes using force-directed layout">
              <Button
                variant="outlined"
                size="small"
                startIcon={<LayoutIcon />}
                onClick={handleLayout}
              >
                Layout
              </Button>
            </Tooltip>
          )}
          {!isReadOnly && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImportImageIcon />}
              onClick={() => setEditorImportOpen(true)}
            >
              Import
            </Button>
          )}
          <Tooltip title="Export map as PNG image">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageIcon />}
              onClick={handleExportImage}
            >
              Export
            </Button>
          </Tooltip>
        </Box>

        <ImportImageDialog
          open={editorImportOpen}
          onClose={() => setEditorImportOpen(false)}
          onImport={handleEditorImport}
          showNameField={false}
        />

        {/* Canvas */}
        <Box ref={reactFlowRef} sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {!isReadOnly && addPanelOpen && (
            <AddNodePanel context={context} onAdd={handleAddNode} onClose={() => setAddPanelOpen(false)} />
          )}

          {selectedNode && (
            <NodeDetailPanel
              node={selectedNode}
              nodes={nodes}
              edges={edges}
              documents={documents}
              context={context}
              isReadOnly={isReadOnly}
              onUpdate={handleNodeUpdate}
              onClose={() => setSelectedNodeId(null)}
              onDelete={isReadOnly ? undefined : handleDeleteNode}
            />
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={isReadOnly ? undefined : handleConnect}
            onNodeClick={handleNodeClick}
            onPaneClick={() => setSelectedNodeId(null)}
            nodeTypes={NODE_TYPES}
            nodesDraggable={!isReadOnly}
            nodesConnectable={!isReadOnly}
            fitView
            fitViewOptions={{ padding: 0.25 }}
            minZoom={0.2}
            maxZoom={2}
            deleteKeyCode={isReadOnly ? null : 'Delete'}
            onEdgeClick={isReadOnly ? undefined : handleEdgeClick}
            defaultEdgeOptions={{ style: { cursor: 'pointer' } }}
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={(n) => {
              const colors = { applicationNode: '#ffa726', outcomeNode: '#42a5f5', databaseNode: '#4db6ac', apiNode: '#ab47bc', fileNode: '#90a4ae' };
              return colors[n.type] || '#ccc';
            }} />
            <Panel position="bottom-right">
              <Legend />
            </Panel>
            {!isReadOnly && (
              <Panel position="top-right">
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={() => setAddPanelOpen((v) => !v)} sx={{ mr: 1 }}>
                  Add Node
                </Button>
              </Panel>
            )}
          </ReactFlow>
        </Box>
      </Box>
    );
  };
}

const DataMapEditor = ReactFlowLib ? makeDataMapInner(ReactFlowLib) : null;

// ── Map card ──────────────────────────────────────────────────────────────────

function MapCard({ map, isReadOnly, onOpen, onDelete, deletingId }) {
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex', flexDirection: 'column', height: '100%',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.3 }}>
          {map.name}
        </Typography>
        {map.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mb: 0.75 }}>
            {map.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled">
          {map.node_count} {map.node_count === 1 ? 'node' : 'nodes'}
          {map.edge_count > 0 && ` · ${map.edge_count} ${map.edge_count === 1 ? 'connection' : 'connections'}`}
        </Typography>
      </CardContent>
      <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {formatDate(map.updated_at)}
        </Typography>
        <Tooltip title="Open">
          <IconButton size="small" onClick={() => onOpen(map)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!isReadOnly && (
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(map)}
                disabled={deletingId === map.id}
              >
                {deletingId === map.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function DataMap({ isReadOnly = false, initialMapId = null, onMapOpened }) {
  const [view, setView] = useState('list');      // 'list' | 'editor'
  const [maps, setMaps] = useState([]);
  const [activeMap, setActiveMap] = useState(null);
  const [context, setContext] = useState({ outcomes: [], applications: [], functions: [] });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchMaps, setSearchMaps] = useState('');

  // Fetch map list + context on mount
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mapsRes, ctxRes, docsRes] = await Promise.all([
        fetch(`${API_BASE}/api/data-maps/`),
        fetch(`${API_BASE}/api/doc-hub/context`),
        fetch(`${API_BASE}/api/doc-hub/documents`),
      ]);
      if (!mapsRes.ok) throw new Error(`Failed to load data maps (${mapsRes.status})`);
      setMaps(await mapsRes.json());
      if (ctxRes.ok) setContext(await ctxRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open a specific map when navigated here from DocHub
  useEffect(() => {
    if (!initialMapId || loading || maps.length === 0) return;
    const target = maps.find((m) => m.id === initialMapId);
    if (target) {
      handleOpenMap(target);
      onMapOpened?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMapId, loading, maps]);

  const handleOpenMap = async (mapSummary) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/data-maps/${mapSummary.id}`);
      if (!res.ok) throw new Error(`Failed to load map (${res.status})`);
      setActiveMap(await res.json());
      setView('editor');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreated = (newMap) => {
    setMaps((prev) => [...prev, { ...newMap, node_count: 0, edge_count: 0 }]);
    setActiveMap(newMap);
    setView('editor');
  };

  const handleSave = async ({ nodes, edges }) => {
    const res = await fetch(`${API_BASE}/api/data-maps/${activeMap.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: activeMap.name, description: activeMap.description, nodes, edges }),
    });
    if (!res.ok) throw new Error(`Save failed (${res.status})`);
    const updated = await res.json();
    setActiveMap(updated);
    setMaps((prev) =>
      prev.map((m) =>
        m.id === updated.id
          ? { ...m, node_count: nodes.length, edge_count: edges.length, updated_at: updated.updated_at }
          : m
      )
    );
  };

  const handleBack = () => {
    setView('list');
    setActiveMap(null);
  };

  const handleImportImage = async ({ name, nodes, edges }) => {
    setError('');
    try {
      const createRes = await fetch(`${API_BASE}/api/data-maps/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: 'Imported from image' }),
      });
      if (!createRes.ok) throw new Error(`Failed to create map (${createRes.status})`);
      const newMap = await createRes.json();
      await fetch(`${API_BASE}/api/data-maps/${newMap.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes, edges }),
      });
      newMap.nodes = nodes;
      newMap.edges = edges;
      handleCreated(newMap);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (map) => {
    if (!window.confirm(`Delete "${map.name}"?`)) return;
    setDeletingId(map.id);
    try {
      const res = await fetch(`${API_BASE}/api/data-maps/${map.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setMaps((prev) => prev.filter((m) => m.id !== map.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Editor view ────────────────────────────────────────────────────────────
  if (view === 'editor' && activeMap) {
    if (!DataMapEditor) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>Back</Button>
          <Typography color="text.secondary" gutterBottom>
            Graph editor requires <code>@xyflow/react</code>.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Run <code>npm install</code> in the <code>frontend/</code> directory to enable this feature.
          </Typography>
        </Box>
      );
    }
    return (
      <DataMapEditor
        map={activeMap}
        context={context}
        documents={documents}
        isReadOnly={isReadOnly}
        onSave={handleSave}
        onBack={handleBack}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Data Maps</Typography>
        <TextField
          size="small"
          placeholder="Search maps…"
          value={searchMaps}
          onChange={(e) => setSearchMaps(e.target.value)}
          sx={{ width: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            endAdornment: searchMaps ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchMaps('')}><ClearIcon sx={{ fontSize: 14 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {!isReadOnly && (
          <>
            <Button
              variant="outlined"
              startIcon={<ImportImageIcon />}
              size="small"
              onClick={() => setImportOpen(true)}
            >
              Import from Image
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              Create Map
            </Button>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : maps.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary" gutterBottom>No data maps yet.</Typography>
            {!isReadOnly && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ mt: 1 }}
              >
                Create your first map
              </Button>
            )}
            {isReadOnly && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Use the Edit toggle (top right) to create data maps.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="center">Nodes</TableCell>
                <TableCell align="center">Connections</TableCell>
                <TableCell>Updated</TableCell>
                {!isReadOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {maps
                .filter((m) =>
                  !searchMaps || m.name.toLowerCase().includes(searchMaps.toLowerCase())
                )
                .map((map) => (
                  <TableRow
                    key={map.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenMap(map.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{map.name}</Typography>
                    </TableCell>
                    <TableCell align="center">{map.node_count ?? 0}</TableCell>
                    <TableCell align="center">{map.edge_count ?? 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {map.updated_at ? new Date(map.updated_at).toLocaleDateString('en-AU') : '—'}
                      </Typography>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Delete map">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deletingId === map.id}
                              onClick={() => handleDelete(map.id)}
                            >
                              {deletingId === map.id
                                ? <CircularProgress size={14} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              {maps.filter((m) =>
                !searchMaps || m.name.toLowerCase().includes(searchMaps.toLowerCase())
              ).length === 0 && (
                <TableRow>
                  <TableCell colSpan={isReadOnly ? 4 : 5} align="center">
                    <Typography variant="caption" color="text.secondary">
                      {searchMaps ? `No maps match "${searchMaps}"` : 'No data maps yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateMapDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreated}
      />

      <ImportImageDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImportImage}
        showNameField
      />
    </Box>
  );
}

export default DataMap;
